import { useEffect, useState, useMemo } from "react";
import { api, AUD } from "../lib/api";
import { toast } from "sonner";
import {
    CheckCircle, Circle, MinusCircle, Paperclip, Trash, Printer,
    FileArrowUp, Plus, WarningCircle, MagicWand, LinkSimple, Copy, ArrowsClockwise,
    Receipt, HandCoins,
} from "@phosphor-icons/react";

/**
 * Yearly Tax Pack — mirrors the accountant's document request as a checklist,
 * auto-populated with existing data where possible. Owner ticks items off,
 * uploads statements/PDFs, adds notes. One-click browser print produces a
 * cover PDF for the pack.
 */

const STATUS_META = {
    outstanding: { label: "Outstanding", cls: "text-[var(--terracotta)]", Icon: Circle },
    received:    { label: "Received",    cls: "text-[var(--eucalyptus)]", Icon: CheckCircle },
    na:          { label: "N/A",         cls: "text-[var(--muted)]",       Icon: MinusCircle },
};

const DEFAULT_FY = 2026;

export default function TaxPack() {
    const [fy, setFy] = useState(DEFAULT_FY);
    const [pack, setPack] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploadingKey, setUploadingKey] = useState(null);
    const [newShare, setNewShare] = useState({ owner: "Penny", company: "", notes: "" });

    const load = async () => {
        const { data } = await api.get(`/tax-pack/${fy}`);
        setPack(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [fy]);

    const sections = useMemo(() => {
        if (!pack) return [];
        const groups = {};
        pack.checklist.forEach(row => {
            if (!groups[row.section]) groups[row.section] = [];
            groups[row.section].push(row);
        });
        return Object.entries(groups);
    }, [pack]);

    const progress = useMemo(() => {
        if (!pack) return { done: 0, total: 0 };
        const total = pack.checklist.filter(r => r.status !== "na").length;
        const done = pack.checklist.filter(r => r.status === "received").length;
        return { done, total };
    }, [pack]);

    const updateRow = (key, patch) => {
        const next = pack.checklist.map(r => r.key === key ? { ...r, ...patch } : r);
        setPack({ ...pack, checklist: next });
    };

    const saveChecklist = async (nextChecklist) => {
        setSaving(true);
        try {
            await api.put(`/tax-pack/${fy}`, { checklist: nextChecklist });
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    const cycleStatus = async (key) => {
        const order = ["outstanding", "received", "na"];
        const row = pack.checklist.find(r => r.key === key);
        const next = order[(order.indexOf(row.status) + 1) % 3];
        const nextChecklist = pack.checklist.map(r => r.key === key ? { ...r, status: next } : r);
        setPack({ ...pack, checklist: nextChecklist });
        await saveChecklist(nextChecklist);
    };

    const commitNotes = async (key) => saveChecklist(pack.checklist);
    const commitUrl = async (key) => saveChecklist(pack.checklist);

    const uploadFile = async (key, file) => {
        if (!file) return;
        setUploadingKey(key);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("checklist_key", key);
            await api.post(`/tax-pack/${fy}/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            toast.success(`Attached ${file.name}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setUploadingKey(null); }
    };

    const saveMetadata = async (patch) => {
        const next = { ...pack.metadata, ...patch };
        setPack({ ...pack, metadata: next });
        try {
            await api.put(`/tax-pack/${fy}`, { metadata: next });
        } catch { toast.error("Save failed"); }
    };

    const addShare = async (e) => {
        e.preventDefault();
        if (!newShare.company.trim()) return;
        try {
            await api.post(`/tax-pack/${fy}/ato-share`, newShare);
            setNewShare({ owner: "Penny", company: "", notes: "" });
            await load();
        } catch { toast.error("Add failed"); }
    };

    const delShare = async (sid) => {
        try {
            await api.delete(`/tax-pack/${fy}/ato-share/${sid}`);
            await load();
        } catch { toast.error("Delete failed"); }
    };

    const [prefilling, setPrefilling] = useState(false);
    const [showDrop, setShowDrop] = useState(false);
    const dropUrl = pack?.drop_token
        ? `${window.location.origin}/drop/${pack.drop_token}` : "";

    const copyDropUrl = async () => {
        try {
            await navigator.clipboard.writeText(dropUrl);
            toast.success("Drop URL copied");
        } catch { toast.error("Copy failed — select and copy manually"); }
    };
    const rotateDropUrl = async () => {
        if (!confirm("Rotating invalidates the current URL. Continue?")) return;
        try {
            await api.post(`/tax-pack/${fy}/rotate-token`);
            await load();
            toast.success("Drop URL rotated");
        } catch { toast.error("Rotate failed"); }
    };

    // Receipts (AI-extracted donations + other deductibles)
    const [receipts, setReceipts] = useState({ items: [], totals: { donation: 0, deduction: 0 } });
    const [uploadingKind, setUploadingKind] = useState(null);

    const loadReceipts = async () => {
        try {
            const { data } = await api.get(`/tax-pack/${fy}/receipts`);
            setReceipts(data);
        } catch { /* no-op */ }
    };
    useEffect(() => { loadReceipts(); /* eslint-disable-next-line */ }, [fy]);

    const uploadReceipt = async (kind, file) => {
        if (!file) return;
        setUploadingKind(kind);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("kind", kind);
            const { data } = await api.post(`/tax-pack/${fy}/receipts/upload`, fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
            });
            const label = data.vendor || file.name;
            const amt = data.amount ? `$${data.amount.toLocaleString()}` : "amount not detected";
            toast.success(`Extracted: ${label} — ${amt}`);
            await Promise.all([loadReceipts(), load()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Receipt upload failed");
        } finally { setUploadingKind(null); }
    };

    const patchReceipt = async (id, patch) => {
        setReceipts(prev => ({ ...prev,
            items: prev.items.map(r => r.id === id ? { ...r, ...patch } : r) }));
        try {
            await api.patch(`/tax-pack/${fy}/receipts/${id}`, patch);
            await Promise.all([loadReceipts(), load()]);
        } catch { toast.error("Save failed"); }
    };

    const delReceipt = async (id) => {
        try {
            await api.delete(`/tax-pack/${fy}/receipts/${id}`);
            await Promise.all([loadReceipts(), load()]);
        } catch { toast.error("Delete failed"); }
    };
    const prefill = async () => {
        setPrefilling(true);
        try {
            const { data } = await api.post(`/tax-pack/${fy}/prefill`);
            const a = data.applied || {};
            const parts = [];
            if (a.interest_count) parts.push(`Interest $${a.interest_total.toLocaleString()} (${a.interest_count} tx)`);
            if (a.donations_count) parts.push(`Donations $${a.donations_total.toLocaleString()} (${a.donations_count} tx)`);
            if (a.wfh_hours) parts.push(`WFH ${a.wfh_hours}h/wk note`);
            toast.success(parts.length ? `Pre-filled: ${parts.join(" · ")}` : "No matching transactions found");
            await load();
        } catch { toast.error("Pre-fill failed"); }
        finally { setPrefilling(false); }
    };

    if (!pack) return <div className="text-[var(--muted)]">Loading…</div>;
    const s = pack.sections || {};

    return (
        <div data-testid="tax-pack-page" className="tax-pack-page">
            <style>{`
                @media print {
                    aside, nav, .no-print { display: none !important; }
                    body { background: white !important; }
                    .tax-pack-page { padding: 0 !important; }
                    .hairline { break-inside: avoid; page-break-inside: avoid; }
                }
            `}</style>

            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Annual Accountant Package</div>
                    <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Tax Pack FY{fy}.</h1>
                    <p className="text-[var(--muted)] text-sm mt-2 max-w-2xl">
                        The document checklist your accountant needs — auto-populated from your data where possible.
                        Tick items off, attach statements, and hit Print to produce a cover PDF.
                    </p>
                </div>
                <div className="flex items-center gap-3 no-print">
                    <label className="text-xs text-[var(--muted)]">FY</label>
                    <select value={fy} onChange={e => setFy(Number(e.target.value))}
                            data-testid="fy-select"
                            className="border border-[var(--line)] px-3 py-2 rounded text-sm">
                        {[2024, 2025, 2026, 2027].map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                    <button onClick={prefill} disabled={prefilling}
                            data-testid="prefill-btn"
                            className="btn-ghost text-sm flex items-center gap-2 disabled:opacity-60"
                            title="Scan FY transactions for interest, donations, WFH">
                        <MagicWand size={14} />
                        {prefilling ? "Scanning…" : "Pre-fill from transactions"}
                    </button>
                    <button onClick={() => window.print()} data-testid="print-pack"
                            className="btn-primary text-sm flex items-center gap-2">
                        <Printer size={14} /> Print / Save as PDF
                    </button>
                </div>
            </div>

            {/* Progress */}
            <div className="hairline bg-white p-6 rounded mb-6" data-testid="tax-pack-progress">
                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <div className="label-eyebrow">Progress</div>
                    <div className="text-sm">
                        <span className="font-mono-num font-semibold text-[var(--eucalyptus)]">{progress.done}</span>
                        <span className="text-[var(--muted)]"> / {progress.total} received</span>
                        {saving && <span className="text-[var(--muted)] ml-3 text-xs">saving…</span>}
                    </div>
                </div>
                <div className="h-2 bg-[var(--line)] rounded overflow-hidden">
                    <div className="h-full bg-[var(--eucalyptus)] transition-all"
                         style={{ width: `${progress.total ? (progress.done / progress.total * 100) : 0}%` }} />
                </div>
            </div>

            {/* Drop URL — private share link for quick uploads from phone */}
            <div className="hairline bg-white p-6 rounded mb-6 no-print" data-testid="drop-url-panel">
                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <LinkSimple size={14} className="text-[var(--eucalyptus)]" />
                        <div className="label-eyebrow">Private drop URL</div>
                    </div>
                    <button onClick={() => setShowDrop(!showDrop)}
                            data-testid="toggle-drop-url"
                            className="text-xs text-[var(--eucalyptus)] hover:underline">
                        {showDrop ? "Hide" : "Show / share"}
                    </button>
                </div>
                {!showDrop ? (
                    <div className="text-xs text-[var(--muted)]">
                        Bookmark this URL on your phone or share it privately — drop PDFs / CSVs
                        straight onto a Tax Pack row from anywhere, no login required.
                    </div>
                ) : (
                    <div>
                        <div className="flex gap-2 flex-wrap items-center">
                            <input readOnly value={dropUrl}
                                   data-testid="drop-url-input"
                                   onFocus={e => e.target.select()}
                                   className="flex-1 min-w-[280px] border border-[var(--line)] px-3 py-2 rounded text-xs font-mono" />
                            <button onClick={copyDropUrl} data-testid="copy-drop-url"
                                    className="btn-ghost text-xs flex items-center gap-1">
                                <Copy size={12} /> Copy
                            </button>
                            <button onClick={rotateDropUrl} data-testid="rotate-drop-url"
                                    className="btn-ghost text-xs flex items-center gap-1 text-[var(--terracotta)]">
                                <ArrowsClockwise size={12} /> Rotate
                            </button>
                        </div>
                        <div className="text-xs text-[var(--muted)] mt-2 leading-relaxed">
                            Treat this like a password — anyone with the URL can upload. Rotate if it leaks.
                            Uploads only <em>attach</em> files; they don&rsquo;t import into your dashboards.
                        </div>
                    </div>
                )}
            </div>

            {/* Auto-populated summary */}
            <div className="grid md:grid-cols-4 gap-4 mb-6">
                <SummaryCard label="Pendaz — Net FY" value={AUD(s.pendaz?.net || 0)}
                             sub={`${s.pendaz?.period?.from} → ${s.pendaz?.period?.to}`}
                             tid="autopop-pendaz" />
                <SummaryCard label="SMSF — Portfolio + Cash"
                             value={AUD((s.smsf?.portfolio_value || 0) + (s.smsf?.cash_ledger_total || 0))}
                             sub={`Portfolio ${AUD(s.smsf?.portfolio_value || 0)} + Cash ${AUD(s.smsf?.cash_ledger_total || 0)}`}
                             tid="autopop-smsf" />
                <SummaryCard label="Personal Investments (live)"
                             value={AUD(s.personal_holdings_total || 0)}
                             sub={`${s.personal_holdings?.length || 0} line(s) via Live Prices`}
                             tid="autopop-personal" />
                <SummaryCard label="Deductions logged"
                             value={AUD(s.deductions?.total || 0)}
                             sub={`${s.deductions?.count || 0} record(s)`}
                             tid="autopop-deductions" tone="green" />
            </div>

            {/* Checklist grouped by section */}
            <div className="space-y-6 mb-8">
                {sections.map(([section, rows]) => (
                    <div key={section} className="hairline bg-white rounded p-6"
                         data-testid={`section-${section.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        <div className="label-eyebrow mb-4">{section}</div>
                        <div className="space-y-3">
                            {rows.map(row => {
                                const meta = STATUS_META[row.status] || STATUS_META.outstanding;
                                const Icon = meta.Icon;
                                return (
                                    <div key={row.key} className="grid md:grid-cols-12 gap-3 items-start pt-3 border-t border-[var(--line)] first:border-t-0 first:pt-0"
                                         data-testid={`row-${row.key}`}>
                                        <button onClick={() => cycleStatus(row.key)}
                                                data-testid={`status-${row.key}`}
                                                title="Click to cycle status"
                                                className={`md:col-span-3 flex items-center gap-2 text-left ${meta.cls} hover:opacity-80`}>
                                            <Icon size={18} weight={row.status === "received" ? "fill" : "regular"} />
                                            <span className="text-xs uppercase tracking-widest">{meta.label}</span>
                                        </button>
                                        <div className="md:col-span-5 text-sm">
                                            {row.label}
                                            {row.file_id && (
                                                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tax/files/${row.file_id}`}
                                                   target="_blank" rel="noopener noreferrer"
                                                   data-testid={`file-link-${row.key}`}
                                                   className="ml-2 inline-flex items-center gap-1 text-xs text-[var(--eucalyptus)] hover:underline">
                                                    <Paperclip size={12} /> file
                                                </a>
                                            )}
                                        </div>
                                        <input type="text" value={row.notes}
                                               placeholder="Notes or URL"
                                               data-testid={`notes-${row.key}`}
                                               onChange={e => updateRow(row.key, { notes: e.target.value })}
                                               onBlur={() => commitNotes(row.key)}
                                               className="md:col-span-3 border border-[var(--line)] px-2 py-1.5 rounded text-xs" />
                                        <div className="md:col-span-1 no-print">
                                            <label className="btn-ghost text-xs cursor-pointer inline-flex items-center gap-1"
                                                   title="Attach file">
                                                {uploadingKey === row.key ? "…" : <FileArrowUp size={14} />}
                                                <input type="file" className="hidden"
                                                       data-testid={`upload-${row.key}`}
                                                       onChange={e => uploadFile(row.key, e.target.files?.[0])} />
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Personal metadata */}
            <div className="hairline bg-white p-6 rounded mb-6" data-testid="personal-metadata">
                <div className="label-eyebrow mb-4">Personal — details for the return</div>
                <div className="grid md:grid-cols-4 gap-4">
                    <MetaField label="WFH hours / week" type="number" step="1"
                               value={pack.metadata.wfh_hours_per_week}
                               onCommit={v => saveMetadata({ wfh_hours_per_week: Number(v) })}
                               tid="meta-wfh" />
                    <MetaField label="Dependents" type="number" step="1"
                               value={pack.metadata.dependents}
                               onCommit={v => saveMetadata({ dependents: Number(v) })}
                               tid="meta-dependents" />
                    <MetaField label="Donations (total $)" type="number" step="1"
                               value={pack.metadata.donations_total}
                               onCommit={v => saveMetadata({ donations_total: Number(v) })}
                               tid="meta-donations" />
                    <MetaField label="Other expenses (free text)" type="text"
                               value={pack.metadata.other_expenses}
                               onCommit={v => saveMetadata({ other_expenses: v })}
                               tid="meta-other" />
                </div>
                <div className="mt-4">
                    <label className="label-eyebrow block mb-2">Notes to accountant</label>
                    <textarea value={pack.metadata.notes || ""} rows={3}
                              data-testid="meta-notes"
                              onChange={e => setPack({ ...pack, metadata: { ...pack.metadata, notes: e.target.value } })}
                              onBlur={e => saveMetadata({ notes: e.target.value })}
                              className="w-full border border-[var(--line)] p-3 rounded text-sm" />
                </div>
            </div>

            {/* ATO Portal shareholdings */}
            <div className="hairline bg-white p-6 rounded mb-6" data-testid="ato-shareholdings">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                    <div>
                        <div className="label-eyebrow">ATO Portal Registered Shareholdings</div>
                        <div className="text-xs text-[var(--muted)] mt-1">
                            Companies showing on the ATO portal but not in Live Prices yet. Add here as a
                            note-only list; wire them into Live Prices once you have unit/price detail.
                        </div>
                    </div>
                </div>
                <table className="editorial w-full mb-4">
                    <thead>
                        <tr><th>Owner</th><th>Company</th><th>Notes</th><th></th></tr>
                    </thead>
                    <tbody>
                        {pack.ato_shareholdings.map(s => (
                            <tr key={s.id} data-testid={`ato-row-${s.id}`}>
                                <td className="text-sm">{s.owner}</td>
                                <td className="text-sm font-medium">{s.company}</td>
                                <td className="text-sm text-[var(--muted)]">{s.notes || "—"}</td>
                                <td className="no-print">
                                    <button onClick={() => delShare(s.id)}
                                            data-testid={`del-ato-${s.id}`}
                                            className="text-[var(--terracotta)] hover:opacity-70">
                                        <Trash size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <form onSubmit={addShare} className="grid md:grid-cols-5 gap-2 items-end no-print"
                      data-testid="ato-add-form">
                    <div>
                        <div className="label-eyebrow mb-1">Owner</div>
                        <select value={newShare.owner} onChange={e => setNewShare({ ...newShare, owner: e.target.value })}
                                data-testid="ato-owner"
                                className="border border-[var(--line)] px-2 py-1.5 rounded text-sm w-full">
                            <option>Darren</option><option>Penny</option><option>Joint</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <div className="label-eyebrow mb-1">Company</div>
                        <input value={newShare.company} onChange={e => setNewShare({ ...newShare, company: e.target.value })}
                               data-testid="ato-company"
                               placeholder="Coles / IAG / …"
                               className="border border-[var(--line)] px-2 py-1.5 rounded text-sm w-full" />
                    </div>
                    <div>
                        <div className="label-eyebrow mb-1">Notes</div>
                        <input value={newShare.notes} onChange={e => setNewShare({ ...newShare, notes: e.target.value })}
                               data-testid="ato-notes"
                               placeholder="e.g. x2 · Joint"
                               className="border border-[var(--line)] px-2 py-1.5 rounded text-sm w-full" />
                    </div>
                    <button type="submit" className="btn-primary text-sm flex items-center justify-center gap-1"
                            data-testid="ato-add-btn">
                        <Plus size={14} /> Add
                    </button>
                </form>
            </div>

            <ReceiptsSection receipts={receipts} onUpload={uploadReceipt}
                             uploadingKind={uploadingKind} onPatch={patchReceipt}
                             onDel={delReceipt} fy={fy} />

            <div className="text-xs text-[var(--muted)] mb-8 flex items-start gap-2 no-print">
                <WarningCircle size={14} className="mt-0.5" />
                <span>
                    Tip: to email the accountant, print this page as PDF (browser Ctrl/Cmd + P → "Save as PDF")
                    and attach the individual statement files. All uploaded attachments are stored securely and
                    can be downloaded via the paperclip link next to each item.
                </span>
            </div>
        </div>
    );
}

/* eslint-disable react/prop-types */
function ReceiptsSection({ receipts, onUpload, uploadingKind, onPatch, onDel, fy }) {
    const donations = receipts.items.filter(r => r.kind === "donation");
    const deducts = receipts.items.filter(r => r.kind === "deduction");
    return (
        <div className="hairline bg-white p-6 rounded mb-6" data-testid="receipts-section">
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                <div>
                    <div className="label-eyebrow">AI Receipts</div>
                    <div className="text-xs text-[var(--muted)] mt-1">
                        Drop a donation receipt or deductible expense — Claude extracts vendor,
                        amount, date. Totals flow into the Tax Pack automatically.
                    </div>
                </div>
                <div className="flex items-baseline gap-4 text-sm">
                    <span className="text-[var(--muted)]">Donations</span>
                    <span className="font-mono-num text-lg" data-testid="receipts-donation-total">
                        ${(receipts.totals?.donation || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                    <span className="text-[var(--muted)]">Deductibles</span>
                    <span className="font-mono-num text-lg" data-testid="receipts-deduction-total">
                        ${(receipts.totals?.deduction || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                    </span>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mb-4 no-print">
                <UploadTile kind="donation" label="Donation receipt" icon={HandCoins}
                            uploading={uploadingKind === "donation"} onUpload={onUpload} />
                <UploadTile kind="deduction" label="Deductible expense" icon={Receipt}
                            uploading={uploadingKind === "deduction"} onUpload={onUpload} />
            </div>

            {receipts.items.length === 0 ? (
                <div className="text-xs text-[var(--muted)] italic py-6 text-center border-t border-[var(--line)]">
                    No receipts yet. Upload a PDF or photo above — takes ~5 seconds to extract.
                </div>
            ) : (
                <>
                    {donations.length > 0 && <ReceiptsTable title="Donations" rows={donations} fy={fy} onPatch={onPatch} onDel={onDel} />}
                    {deducts.length > 0 && <ReceiptsTable title="Other Deductibles" rows={deducts} fy={fy} onPatch={onPatch} onDel={onDel} />}
                </>
            )}
        </div>
    );
}

function UploadTile({ kind, label, icon: Icon, uploading, onUpload }) {
    return (
        <label className={`border-2 border-dashed rounded p-4 flex items-center gap-3 cursor-pointer transition-colors ${
            uploading ? "border-[var(--eucalyptus)] bg-[var(--eucalyptus)]/10" : "border-[var(--line)] hover:border-[var(--eucalyptus)]"}`}
               data-testid={`upload-tile-${kind}`}>
            <Icon size={24} className="text-[var(--eucalyptus)] shrink-0" />
            <div className="flex-1">
                <div className="text-sm font-medium">{uploading ? "Extracting…" : `Upload ${label}`}</div>
                <div className="text-xs text-[var(--muted)]">PDF, JPG, PNG · up to 25MB</div>
            </div>
            <input type="file" className="hidden"
                   data-testid={`upload-input-${kind}`}
                   accept=".pdf,.png,.jpg,.jpeg,.webp,.heic"
                   onChange={e => onUpload(kind, e.target.files?.[0])} />
        </label>
    );
}

function ReceiptsTable({ title, rows, fy, onPatch, onDel }) {
    return (
        <div className="mb-4" data-testid={`receipts-table-${title.toLowerCase().replace(/\s+/g,"-")}`}>
            <div className="label-eyebrow mb-2">{title}</div>
            <table className="editorial w-full text-sm">
                <thead>
                    <tr>
                        <th style={{width:"25%"}}>Vendor</th>
                        <th style={{width:"15%"}}>Amount</th>
                        <th style={{width:"15%"}}>Date</th>
                        <th>Notes</th>
                        <th style={{width:"90px"}}></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(r => (
                        <tr key={r.id} data-testid={`receipt-row-${r.id}`}>
                            <td>
                                <input value={r.vendor || ""}
                                       onChange={e => onPatch(r.id, { vendor: e.target.value })}
                                       onBlur={e => onPatch(r.id, { vendor: e.target.value })}
                                       className="w-full border-none bg-transparent text-sm" />
                            </td>
                            <td>
                                <input type="number" step="0.01" value={r.amount || 0}
                                       onChange={e => onPatch(r.id, { amount: Number(e.target.value) })}
                                       onBlur={e => onPatch(r.id, { amount: Number(e.target.value) })}
                                       className="w-full border-none bg-transparent text-sm font-mono-num" />
                            </td>
                            <td>
                                <input type="date" value={r.date || ""}
                                       onChange={e => onPatch(r.id, { date: e.target.value })}
                                       onBlur={e => onPatch(r.id, { date: e.target.value })}
                                       className="w-full border-none bg-transparent text-sm font-mono-num" />
                            </td>
                            <td>
                                <div className="text-xs text-[var(--muted)]">{r.notes || r.category_hint}</div>
                            </td>
                            <td className="text-right no-print">
                                {r.file_id && (
                                    <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/tax/files/${r.file_id}`}
                                       target="_blank" rel="noopener noreferrer"
                                       data-testid={`receipt-file-${r.id}`}
                                       className="text-[var(--eucalyptus)] hover:opacity-80 mr-3">
                                        <Paperclip size={14} className="inline" />
                                    </a>
                                )}
                                <button onClick={() => onDel(r.id)}
                                        data-testid={`receipt-del-${r.id}`}
                                        className="text-[var(--terracotta)] hover:opacity-70">
                                    <Trash size={14} />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
/* eslint-enable react/prop-types */

function SummaryCard({ label, value, sub, tid, tone }) {
    const bg = tone === "green" ? "var(--eucalyptus)" : "white";
    const fg = tone === "green" ? "var(--bone)" : "var(--ink)";
    return (
        <div data-testid={tid} className="p-5 rounded hairline"
             style={{ background: bg, color: fg }}>
            <div className="label-eyebrow" style={{ opacity: tone ? 0.8 : 1 }}>{label}</div>
            <div className="font-serif text-2xl mt-2 font-mono-num">{value}</div>
            {sub && <div className="text-xs mt-1 opacity-80">{sub}</div>}
        </div>
    );
}

function MetaField({ label, value, onCommit, type, step, tid }) {
    const [v, setV] = useState(value ?? "");
    useEffect(() => { setV(value ?? ""); }, [value]);
    return (
        <label className="block">
            <div className="label-eyebrow mb-1">{label}</div>
            <input type={type} step={step} value={v}
                   data-testid={tid}
                   onChange={e => setV(e.target.value)}
                   onBlur={() => onCommit(v)}
                   className="w-full border border-[var(--line)] px-3 py-2 rounded text-sm font-mono-num" />
        </label>
    );
}
